<template>
  <a :class="buttonClass" @click="startImport()" v-if="isInitialized">
    <img src="/assets/icons/csv-icon.png">
    Upload CSV File
  </a>
</template>
<script>
import { customFieldsQuery } from '../../graphql/customFields'
import {
  flatfileImporter,
  emailValidator,
  booleanValidator,
  isTruthy,
  isFalsy,
  addTrimRecordHook,
  decimalValidator,
  addDateFieldHook,
  contactPhoneTypeValidator,
  contactEmailTypeValidator,
  addNameFieldsHook
} from '../../services/FlatfileService'

export default {
  props: {
    buttonClass: [String, Object]
  },
  data () {
    // create a promise to wait for the custom fields to be imported
    let customFieldsResolver
    const customFieldsPromise = new Promise((resolve) => {
      customFieldsResolver = resolve
    })

    return {
      importer: null,
      isInitialized: false,
      customFieldsPromise: customFieldsPromise,
      customFieldsResolver: customFieldsResolver
    }
  },
  apollo: {
    customFields: {
      query: customFieldsQuery,
      result ({ data }) {
        if (data) {
          // resolve the custom fields promise, once the fields have loaded
          this.customFieldsResolver()
        }
      }
    }
  },
  methods: {
    /**
     * Open the importer
     */
    async startImport () {
      // wait for the data to be uploaded
      const results = await this.importer.requestDataFromUser()
      let data = results.data
      // format the data
      data = this.parseData(data)
      // emit the data
      this.emitData(data)
      // close the importer
      this.importer.close()
    },
    /**
     * Format the CSV data into the structure that our API expects it
     * @param data
     * @return {{}}
     */
    parseData (data) {
      return data.map((row) => {
        const formatted = {
          firstName: row.firstName,
          lastName: row.lastName || null,
          salutation: row.salutation || null,
          fullName: row.fullName || null,
          organizationName: row.organizationName || null,
          isOrganization: row.isOrganization || null,
          occupation: row.occupation || null,
          employer: row.employer || null,
          bio: row.bio || null,
          askAmount: row.askAmount || row.askAmount === 0 ? row.askAmount : null,
          ngpId: row.ngpId || null,
          emails: [],
          phones: [],
          addresses: [],
          customFields: row.customFields || null
        }
        // convert phones and emails to objects that include the type
        for (let key in row) {
          let value = row[key]
          // Emails & Phones
          // parse the key to determine the type of record and push it into an array of records
          if (key.substr(0, 6) === 'email_' && value) {
            // parse out the record type
            const type = key.substr(6)
            // build the record data object to push into our array
            const rowData = {
              email: value,
              type: type
            }
            // verified flag
            const typeVerified = row[`emailVerified_${type}`]
            if (isTruthy(typeVerified) || isFalsy(typeVerified)) {
              rowData.isVerified = isTruthy(typeVerified)
            }
            // primary record?
            const primaryType = row.primaryEmailType
            if (primaryType && primaryType.toUpperCase() === type) {
              rowData.isPrimary = true
            }
            // push to array
            formatted.emails.push(rowData)
          }
          // phones
          if (key.substr(0, 6) === 'phone_' && value) {
            // parse out the record type
            const type = key.substr(6)
            // build the record data object to push into our array
            const rowData = {
              number: value,
              type: type
            }
            // verified flag
            const typeVerified = row[`phoneVerified_${type}`]
            if (isTruthy(typeVerified) || isFalsy(typeVerified)) {
              rowData.isVerified = isTruthy(typeVerified)
            }
            // primary record?
            const primaryType = row.primaryPhoneType
            if (primaryType && primaryType.toUpperCase() === type) {
              rowData.isPrimary = true
            }
            // push to array
            formatted.phones.push(rowData)
          }
        }
        // convert the address to an array
        if (row.addressLine1 || row.addressLine2 || row.city || row.state || row.zipCode || row.country) {
          formatted.addresses.push({
            line1: row.addressLine1 || null,
            line2: row.addressLine2 || null,
            city: row.city || null,
            state: row.state || null,
            zipCode: row.zipCode || null,
            country: row.country || null
          })
        }
        // map the custom fields to objects
        formatted.customFields = []
        Object.keys(row).forEach(key => {
          const substrLen = 'customField'.length
          if (key.substr(0, substrLen) === 'customField' && row[key]) {
            formatted.customFields.push({
              custom_field_id: parseInt(key.substr(substrLen)),
              value: JSON.stringify(row[key]) // stringify the value, because we stringify all custom field values
            })
          }
        })
        // return the new record
        return formatted
      })
    },
    /**
     * Load the importer
     */
    async initializeImporter () {
      // keep track of fields that we need to add hooks to
      const dateFields = []
      // create the allowed fields
      const fields = [
        {
          label: 'First Name',
          key: 'firstName',
          validators: [{
            validate: 'required_without_all',
            fields: ['fullName', 'organizationName']
          }],
          alternates: ['first']
        },
        {
          label: 'Last Name',
          key: 'lastName',
          alternates: ['surname', 'family name']
        },
        {
          label: 'Salutation',
          key: 'salutation',
          alternates: ['nickname', 'nick name']
        },
        {
          label: 'Full Name',
          key: 'fullName',
          validators: [{
            validate: 'required_without_all',
            fields: ['firstName', 'organizationName']
          }]
        },
        {
          label: 'Organization Name',
          key: 'organizationName',
          validators: [{
            validate: 'required_without_all',
            fields: ['fullName', 'firstName']
          }],
          alternates: ['company name', 'company', 'business name']
        },
        {
          label: 'Contact is Organization',
          key: 'isOrganization',
          validators: [booleanValidator]
        },
        {
          label: 'Occupation / Job Title',
          key: 'occupation',
          alternates: ['job']
        },
        {
          label: 'Employer',
          key: 'employer'
        },
        {
          label: 'Bio',
          key: 'bio',
          alternates: ['biography', 'notes', 'summary']
        },
        {
          label: 'Home Email',
          key: 'email_HOME',
          validators: [emailValidator]
        },
        {
          label: 'Home Email Verified?',
          key: 'emailVerified_HOME',
          validators: [booleanValidator]
        },
        {
          label: 'Work Email',
          key: 'email_WORK',
          validators: [emailValidator]
        },
        {
          label: 'Work Email Verified?',
          key: 'emailVerified_WORK',
          validators: [booleanValidator]
        },
        {
          label: 'Other Email',
          key: 'email_OTHER',
          validators: [emailValidator]
        },
        {
          label: 'Other Email Verified?',
          key: 'emailVerified_OTHER',
          validators: [booleanValidator]
        },
        {
          label: 'Primary Email Type',
          key: 'primaryEmailType',
          validators: [contactEmailTypeValidator]
        },
        {
          label: 'Home Phone',
          key: 'phone_HOME'
        },
        {
          label: 'Home Phone Verified?',
          key: 'phoneVerified_HOME',
          validators: [booleanValidator]
        },
        {
          label: 'Work Phone',
          key: 'phone_WORK'
        },
        {
          label: 'Work Phone Verified?',
          key: 'phoneVerified_WORK',
          validators: [booleanValidator]
        },
        {
          label: 'Mobile Phone',
          key: 'phone_MOBILE'
        },
        {
          label: 'Mobile Phone Verified?',
          key: 'phoneVerified_MOBILE',
          validators: [booleanValidator]
        },
        {
          label: 'Other Phone',
          key: 'phone_OTHER'
        },
        {
          label: 'Other Phone Verified?',
          key: 'phoneVerified_OTHER',
          validators: [booleanValidator]
        },
        {
          label: 'Primary Phone Type',
          key: 'primaryPhoneType',
          validators: [contactPhoneTypeValidator]
        },
        {
          label: 'Street Address',
          key: 'addressLine1',
          alternates: ['address']
        },
        {
          label: 'Street Address 2',
          key: 'addressLine2'
        },
        {
          label: 'City',
          key: 'city'
        },
        {
          label: 'State',
          key: 'state'
        },
        {
          label: 'Zip Code',
          key: 'zipCode',
          alternates: ['zip']
        },
        {
          label: 'Country',
          key: 'country'
        },
        {
          label: 'Address Verified?',
          key: 'addressVerified',
          validators: [booleanValidator]
        },
        {
          label: 'Ask Amount',
          key: 'askAmount',
          alternates: ['ask', 'ask amount'],
          validators: [decimalValidator]
        },
        {
          label: 'NGP Contact ID',
          key: 'ngpId'
        }
      ]
      // add the custom fields
      if (this.customFields) {
        this.customFields.forEach(customField => {
          const fieldKey = 'customField' + customField.id
          const fieldData = {
            label: customField.label,
            key: fieldKey,
            alternates: [customField.key, customField.label]
          }
          // limit the input options if the custom field has options
          if (customField.format === 'Select' || customField.format === 'Checkboxes') {
            fieldData.type = 'select'
            fieldData.options = (customField.options || []).map(opt => {
              return { value: opt, label: opt }
            })
          }
          // add a validator based on the format
          if (customField.format === 'Number') {
            fieldData.validators = [decimalValidator]
          }
          if (customField.format === 'Date') {
            // date fields validation is handled by a hook which has to be added after the importer has been instantiated
            dateFields.push(fieldKey)
          }
          // push the custom field onto the fields array
          fields.push(fieldData)
        })
      }
      // set the fields to the importer
      this.importer = await flatfileImporter({
        fields: fields,
        allowCustom: false,
        type: 'Contact'
      })
      // add a "trim" hook to trim whitespace
      addTrimRecordHook(this.importer)
      // add a hook to check for required names
      addNameFieldsHook(this.importer)
      // add date validation hooks
      dateFields.forEach(fieldName => addDateFieldHook(this.importer, fieldName))
      // mark as initialized
      this.isInitialized = true
    },
    /**
     * Emit the imported data to the parent component
     * @param data
     */
    emitData (data) {
      // format the response
      const response = {
        source: 'csv',
        sourceMeta: null,
        contacts: data
      }
      // emit the response
      this.$emit('data', response)
    }
  },
  created () {
    // wait for the custom fields to load, and then initialize the importer
    this.customFieldsPromise.then(async () => {
      await this.initializeImporter()
    })
  }
}
</script>
